'use client';

import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import clsx from "clsx";
import smoothScrollToElement from "@/utils/smoothScroll";

const TabNavigation = forwardRef((props, ref) => {
  const t = useTranslationDetector('hotel');
  const tabs = [
    { id: 'overview', label: t('tabOverview') },
    { id: 'rooms', label: t('tabRooms') },
    { id: 'features', label: t('tabFeatures') },
    { id: 'policies', label: t('tabPolicies') },
    { id: 'info', label: t('tabInfo') },
  ];
  const buttonsListRef = useRef(null);

  useEffect(() => {
    // Scroll button list to make clicked button visible
    if (buttonsListRef.current) {
      const targetButton = document.querySelector(`#tab_button_${props.activeSection}`);
      if (targetButton) {
        const container = buttonsListRef.current;
        const containerRect = container.getBoundingClientRect();
        const buttonRect = targetButton.getBoundingClientRect();
        const scrollLeft = container.scrollLeft;
        const containerWidth = containerRect.width;
        const buttonLeft = buttonRect.left - containerRect.left + scrollLeft;
        const buttonWidth = buttonRect.width;

        // Center the button if possible, otherwise just ensure it's visible
        const targetScroll = buttonLeft - containerWidth / 2 + buttonWidth / 2;
        container.scrollTo({ left: targetScroll, behavior: "smooth" });
      }
    }
  }, [buttonsListRef, props.activeSection]);

  const handleTabClick = useCallback(
    (tabId) => {
      if (tabId === props.activeSection) return;

      const target = document.getElementById(tabId);

      if (target) {
        smoothScrollToElement(target, 120, 0);
      } else if (typeof props.onChangeActiveSection === "function") {
        // Fallback: if section isn't found, still allow parent state update.
        props.onChangeActiveSection(tabId);
      }
    },
    [props.activeSection, props.onChangeActiveSection],
  );

  return (
    <nav ref={ref} className={clsx("border-b border-border-weak", props.className)}>
      <ul ref={buttonsListRef} className='flex overflow-x-auto scrollbar-hide'>
        {tabs.map((tab) => {
          const isActive = props.activeSection === tab.id

          return (
            <li key={tab.id} className='-mb-px'>
              <button
                id={`tab_button_${tab.id}`}
                type='button'
                onClick={() => handleTabClick(tab.id)}
                className={clsx(
                  'relative md:py-[20px] pt-[6px] pb-[14px] px-4 cursor-pointer text-sm leading-19 sm:text-base sm:leading-21 whitespace-nowrap',
                  isActive
                    ? 'font-semibold text-primary after:content-[""] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-t-[10px]'
                    : 'font-medium text-foreground'
                )}
              >
                {tab.label}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
});

export default TabNavigation;